import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { UseFormReturn } from "react-hook-form";
import {
  BarChart3,
  Euro,
  TrendingUp,
  Users,
  Wallet,
  Target,
  Megaphone,
  Lightbulb,
  Building2,
  Heart,
  UserCog,
  Sparkles,
} from "lucide-react";

interface NumbersGoalsStaffStepProps {
  form: UseFormReturn<any>;
}

const RadioRow = ({ value, label }: { value: string; label: string }) => (
  <label className="flex items-center gap-3 rounded-md border border-input bg-background px-3 py-2 cursor-pointer hover:bg-muted/50 transition-colors">
    <RadioGroupItem value={value} />
    <span className="text-sm">{label}</span>
  </label>
);

const NumbersGoalsStaffStep = ({ form }: NumbersGoalsStaffStepProps) => {
  return (
    <div className="space-y-8 animate-fade-in">
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-primary" />
          Numeri, Obiettivi & Staff
        </h2>
        <p className="text-muted-foreground">
          Aiutaci a capire meglio i numeri, gli obiettivi e la struttura del tuo centro
        </p>
      </div>

      {/* SEZIONE NUMERI */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 pb-2 border-b">
          <Euro className="w-5 h-5 text-primary" />
          Numeri del centro
        </h3>

        <FormField
          control={form.control}
          name="monthlyRevenueAvg"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Qual è il fatturato mensile medio del centro? *</FormLabel>
              <FormControl>
                <Input placeholder="Es. 15.000 €" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="doesBasicAesthetics"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Fai estetica di base? *
              </FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  className="grid sm:grid-cols-2 gap-2"
                >
                  <RadioRow value="yes" label="Sì" />
                  <RadioRow value="no" label="No" />
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="revenueSplitBasicVsAdvanced"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Quanta percentuale del tuo fatturato proviene da Estetica di base e quanto da Estetica Avanzata? *
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Es. 40% da estetica di base, 60% da estetica avanzata"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="revenueSplitAdvancedTreatments"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Per i trattamenti di estetica avanzata, indica la percentuale derivante da trattamenti VISO, LASER e CORPO *
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Es. 30% corpo, 50% laser, 20% viso"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="monthlyProfitAvg"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Qual è l'utile medio mensile del centro? *
              </FormLabel>
              <FormControl>
                <Input placeholder="Es. 3.500 €" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="ownerSalaryConsistency"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Wallet className="w-4 h-4" /> Riesci tu come titolare ad avere uno stipendio tutti i mesi? *
              </FormLabel>
              <FormControl>
                <Input placeholder="Es. Sì, 2.000 € al mese / A volte / No" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="revenueSplitNewVsReturning"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Users className="w-4 h-4" /> Indica la % di fatturato mensile che deriva da NUOVI clienti e da VECCHI clienti *
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Es. 30% da nuovi e 70% da vecchi"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* SEZIONE MARKETING & OBIETTIVI */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 pb-2 border-b">
          <Target className="w-5 h-5 text-primary" />
          Marketing e obiettivi
        </h3>

        <FormField
          control={form.control}
          name="internalMarketingActions"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Megaphone className="w-4 h-4" /> Che azioni di marketing interno fate per ri-monetizzare i vostri clienti? *
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Es. chiamate a scadenza pacchetto, messaggi con promo mensili, newsletter, ecc."
                  className="resize-none min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="otherActionsForGoals"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4" /> Oltre all'acquisizione di nuova clientela, che altre azioni/cambiamenti hai in mente per raggiungere i tuoi obiettivi? *
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Es. comprare nuovi macchinari, assumere personale, ristrutturare, ecc."
                  className="resize-none min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="goalWithSaloneVincente"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Target className="w-4 h-4" /> Indica l'obiettivo che ti sei prefissata di raggiungere insieme a Salone Vincente *
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Es. raddoppiare il fatturato in 12 mesi, riempire l'agenda, aprire una seconda sede..."
                  className="resize-none min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="whyThisGoal"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Come mai proprio questo obiettivo? Raggiungerlo cosa ti permetterebbe di fare che ora non puoi? *
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Racconta il motivo profondo dietro questo obiettivo..."
                  className="resize-none min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* SEZIONE STAFF */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 pb-2 border-b">
          <Building2 className="w-5 h-5 text-primary" />
          Struttura e staff
        </h3>

        <FormField
          control={form.control}
          name="staffStructure"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Come è strutturato il centro? *
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Numero di cabine, dipendenti (quanti full-time e quanti part-time)..."
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="staffRelationship"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Heart className="w-4 h-4" /> Che rapporto hai con il tuo staff? *
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Racconta in modo ampio ed esaustivo la situazione nel centro..."
                  className="resize-none min-h-[120px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="ownerOperationalRole"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <UserCog className="w-4 h-4" /> TU come titolare sei operativa? *
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Vendi percorsi, svolgi trattamenti in cabina, ti occupi solo della gestione..."
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export default NumbersGoalsStaffStep;
